import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowRight, X, Building2, TrendingUp, MapPin, Maximize, Clock, ShieldCheck, IndianRupee, Percent } from "lucide-react";
import { api } from "@/lib/api";

const HERO_IMG = "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODh8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBjb3Jwb3JhdGUlMjB3ZWFsdGglMjBtYW5hZ2VtZW50fGVufDB8fHx8MTc4OTM5NDIwOHww&ixlib=rb-4.1.0&q=85";

const LISTINGS = [
  {
    id: "cp-1", name: "Meridian Trade Tower", type: "Office", location: "Bandra-Kurla Complex, Mumbai",
    area: "12,400 sq ft", price: "₹8.6 Cr", yield: "7.2%", status: "Pre-Leased",
    img: "https://images.unsplash.com/photo-1560179707-f14e90ef3623?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODh8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBjb3Jwb3JhdGUlMjB3ZWFsdGglMjBtYW5hZ2VtZW50fGVufDB8fHx8MTc4OTM5NDIwOHww&ixlib=rb-4.1.0&q=85",
  },
  {
    id: "cp-2", name: "Palm Court High-Street Retail", type: "Retail", location: "Linking Road, Bandra West",
    area: "3,200 sq ft", price: "₹4.1 Cr", yield: "6.8%", status: "Ready to Move",
    img: "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODh8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBjb3Jwb3JhdGUlMjB3ZWFsdGglMjBtYW5hZ2VtZW50fGVufDB8fHx8MTc4OTM5NDIwOHww&ixlib=rb-4.1.0&q=85",
  },
  {
    id: "cp-3", name: "Gateway Logistics Hub", type: "Warehousing", location: "Bhiwandi, Thane",
    area: "48,000 sq ft", price: "₹9.2 Cr", yield: "8.1%", status: "Pre-Leased",
    img: "https://images.unsplash.com/photo-1560179707-f14e90ef3623?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODh8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBjb3Jwb3JhdGUlMjB3ZWFsdGglMjBtYW5hZ2VtZW50fGVufDB8fHx8MTc4OTM5NDIwOHww&ixlib=rb-4.1.0&q=85",
  },
  {
    id: "cp-4", name: "Vertex Tech Park Floor", type: "Office", location: "Hinjawadi Phase 2, Pune",
    area: "18,500 sq ft", price: "₹11.3 Cr", yield: "7.5%", status: "Fit-Out Ready",
    img: "https://images.pexels.com/photos/8135492/pexels-photo-8135492.jpeg",
  },
];

const FILTERS = ["All", "Office", "Retail", "Pre-Leased", "Warehousing"];

const BrandLockup = () => (
  <div className="flex items-center gap-3" data-testid="commercial-brand">
    <img src="/logo.jpeg" alt="Keyscape Advisory Services LLP" className="h-10 w-auto rounded-md bg-white px-1.5 py-0.5 object-contain" />
    <span className="leading-tight">
      <span className="block text-sm font-medium text-white">Keyscape Advisory Services LLP</span>
      <span className="block font-plexmono text-[9px] uppercase tracking-[0.3em] text-neutral-500">Commercial Property</span>
    </span>
  </div>
);

const inputCls = "w-full bg-white/[0.03] border border-white/10 px-3 py-2.5 text-sm text-neutral-100 outline-none focus:border-[#D9A441] transition-colors rounded-none placeholder:text-neutral-600";

const StatCard = ({ icon: Icon, label, value, testId }) => (
  <div className="border border-white/10 bg-white/[0.02] p-5" data-testid={testId}>
    <div className="flex items-center justify-between">
      <p className="font-plexmono text-[9px] uppercase tracking-[0.25em] text-neutral-500">{label}</p>
      <Icon size={14} className="text-[#D9A441]" />
    </div>
    <p className="text-2xl md:text-3xl font-light text-white mt-3">{value}</p>
  </div>
);

const Param = ({ icon: Icon, label, value }) => (
  <div className="flex items-center gap-2">
    <Icon size={13} className="text-[#D9A441] shrink-0" />
    <span className="font-plexmono text-[10px] text-neutral-500 uppercase tracking-[0.12em]">{label}</span>
    <span className="font-plexmono text-[11px] text-white ml-auto">{value}</span>
  </div>
);

export default function CommercialProperty() {
  const [filter, setFilter] = useState("All");
  const [enquiring, setEnquiring] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);

  const listings = LISTINGS.filter((l) => filter === "All" || l.type === filter || l.status === filter);

  const submitEnquiry = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const res = await api.post("/leads", {
        name: form.name,
        email: form.email,
        phone: form.phone || null,
        interest: "commercial",
        budget: null,
        message: `Commercial enquiry — ${enquiring.name} (${enquiring.location}). ${form.message}`.trim(),
      });
      toast.success(
        res.data.status === "qualified"
          ? "You're pre-qualified. Our commercial desk will reach out within hours."
          : "Enquiry received. Our commercial desk will be in touch shortly."
      );
      setEnquiring(null);
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0B] font-plex text-neutral-200" data-testid="commercial-page">
      <header className="sticky top-0 z-40 bg-[#0A0A0B]/85 backdrop-blur border-b border-white/10">
        <div className="max-w-[1500px] mx-auto flex items-center justify-between px-6 md:px-10 py-4">
          <Link to="/" data-testid="commercial-home-brand"><BrandLockup /></Link>
          <nav className="flex items-center gap-6">
            <Link to="/" data-testid="commercial-home-link" className="font-plexmono text-[10px] uppercase tracking-[0.25em] text-neutral-500 hover:text-[#D9A441] transition-colors">Home</Link>
            <button
              onClick={() => setEnquiring(LISTINGS[0])}
              data-testid="commercial-enquire-top"
              className="bg-[#D9A441] hover:bg-[#c8943a] text-black px-5 py-2.5 font-plexmono text-[10px] uppercase tracking-[0.2em] font-semibold transition-colors"
            >
              Enquire
            </button>
          </nav>
        </div>
      </header>

      <section className="relative h-[58vh] min-h-[420px] overflow-hidden border-b border-white/10" data-testid="commercial-hero">
        <img src={HERO_IMG} alt="Grade-A commercial towers" className="absolute inset-0 w-full h-full object-cover opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0B] via-[#0A0A0B]/40 to-[#0A0A0B]/70" />
        <div className="relative z-10 h-full max-w-[1500px] mx-auto px-6 md:px-10 flex flex-col justify-end pb-14">
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }} className="font-plexmono text-[10px] uppercase tracking-[0.35em] text-[#D9A441] mb-5">
            Commercial Desk — Income Assets
          </motion.p>
          <div className="overflow-hidden">
            <motion.h1
              initial={{ y: "105%" }}
              animate={{ y: 0 }}
              transition={{ duration: 1, ease: [0.22, 1, 0.36, 1], delay: 0.4 }}
              className="text-4xl sm:text-5xl lg:text-6xl font-medium text-white tracking-tight max-w-3xl leading-[1.08]"
            >
              Commercial assets that pay you back.
            </motion.h1>
          </div>
          <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.8 }} className="mt-5 max-w-xl text-sm md:text-base text-neutral-400 leading-relaxed">
            Grade-A offices, high-street retail and pre-leased warehousing — underwritten for yield,
            diligenced for title, and placed with tenants before you sign.
          </motion.p>
        </div>
      </section>

      <section className="max-w-[1500px] mx-auto px-6 md:px-10 py-8 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="commercial-stats">
        <StatCard icon={Percent} label="Indicative Rental Yields" value="6.8 – 8.1%" testId="cp-stat-yield" />
        <StatCard icon={Clock} label="Term-Sheet Turnaround" value="72 hrs" testId="cp-stat-turnaround" />
        <StatCard icon={ShieldCheck} label="Title-Diligenced Inventory" value="100%" testId="cp-stat-title" />
        <StatCard icon={IndianRupee} label="Pricing Convention" value="Cr / L" testId="cp-stat-pricing" />
      </section>

      <section className="max-w-[1500px] mx-auto px-6 md:px-10 py-10" data-testid="commercial-listings">
        <div className="flex flex-wrap items-center justify-between gap-6 mb-8">
          <div>
            <p className="font-plexmono text-[9px] uppercase tracking-[0.3em] text-[#D9A441]">Current Inventory</p>
            <h2 className="text-2xl md:text-3xl font-medium text-white mt-1">Available Assets</h2>
          </div>
          <div className="flex flex-wrap gap-2" data-testid="commercial-filters">
            {FILTERS.map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                data-testid={`filter-${f.toLowerCase().replace(/\s/g, "-")}`}
                className={`px-4 py-2 font-plexmono text-[10px] uppercase tracking-[0.2em] border transition-colors duration-300 ${
                  filter === f ? "border-[#D9A441] text-[#D9A441] bg-[#D9A441]/5" : "border-white/10 text-neutral-500 hover:text-white"
                }`}
              >
                {f}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {listings.map((l, i) => (
            <motion.article
              key={l.id}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.6, delay: (i % 2) * 0.08 }}
              className="border border-white/10 bg-white/[0.02] group"
              data-testid={`listing-${l.id}`}
            >
              <div className="aspect-[16/9] overflow-hidden">
                <img src={l.img} alt={l.name} loading="lazy" className="w-full h-full object-cover opacity-90 transition-transform duration-700 group-hover:scale-105" />
              </div>
              <div className="p-6">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h3 className="text-xl font-medium text-white">{l.name}</h3>
                    <p className="font-plexmono text-[10px] uppercase tracking-[0.2em] text-neutral-500 mt-1.5 flex items-center gap-1.5">
                      <MapPin size={11} className="text-[#D9A441]" /> {l.location}
                    </p>
                  </div>
                  <span className="shrink-0 font-plexmono text-[9px] uppercase tracking-[0.15em] px-2.5 py-1 border border-[#D9A441]/40 text-[#D9A441]">{l.status}</span>
                </div>
                <div className="grid grid-cols-1 gap-2.5 mt-5 pt-5 border-t border-white/5">
                  <Param icon={Maximize} label="Carpet Area" value={l.area} />
                  <Param icon={Building2} label="Asset Class" value={l.type} />
                  <Param icon={TrendingUp} label="Rental Yield" value={l.yield} />
                </div>
                <div className="flex items-center justify-between mt-6">
                  <p className="text-2xl font-light text-white" data-testid={`price-${l.id}`}>{l.price}</p>
                  <button
                    onClick={() => setEnquiring(l)}
                    data-testid={`enquire-${l.id}`}
                    className="border border-white/15 hover:border-[#D9A441] hover:text-[#D9A441] text-neutral-300 group/btn flex items-center gap-2 px-5 py-2.5 font-plexmono text-[10px] uppercase tracking-[0.2em] transition-colors"
                  >
                    Enquire
                    <ArrowRight size={13} className="transition-transform duration-300 group-hover/btn:translate-x-1" />
                  </button>
                </div>
              </div>
            </motion.article>
          ))}
        </div>
        <p className="font-plexmono text-[9px] text-neutral-600 uppercase tracking-[0.2em] mt-8">
          Representative inventory — live availability shared on enquiry with the commercial desk.
        </p>
      </section>

      <footer className="border-t border-white/10 mt-6">
        <div className="max-w-[1500px] mx-auto px-6 md:px-10 py-8 flex items-center justify-between">
          <BrandLockup />
          <p className="font-plexmono text-[9px] text-neutral-600 uppercase tracking-[0.2em]">© 2026 Keyscape Advisory Services LLP</p>
        </div>
      </footer>

      {enquiring && (
        <div className="fixed inset-0 z-50" data-testid="commercial-enquiry-overlay">
          <div className="absolute inset-0 bg-black/70" onClick={() => setEnquiring(null)} />
          <aside className="absolute inset-y-0 right-0 w-full max-w-md bg-[#111112] border-l border-white/10 p-8 overflow-y-auto font-plex" data-testid="commercial-enquiry-drawer">
            <div className="flex items-start justify-between mb-8">
              <div>
                <p className="font-plexmono text-[10px] uppercase tracking-[0.3em] text-[#D9A441]">Commercial Enquiry</p>
                <h2 className="text-2xl font-medium text-white mt-2">{enquiring.name}</h2>
                <p className="font-plexmono text-[10px] text-neutral-500 mt-1">{enquiring.location}</p>
              </div>
              <button onClick={() => setEnquiring(null)} data-testid="commercial-enquiry-close" className="text-neutral-500 hover:text-white transition-colors"><X size={18} /></button>
            </div>
            <form onSubmit={submitEnquiry} className="space-y-5" data-testid="commercial-enquiry-form">
              <input required placeholder="Full name" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} className={inputCls} data-testid="commercial-name" />
              <input required type="email" placeholder="Email address" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} className={inputCls} data-testid="commercial-email" />
              <input placeholder="Phone (speeds up qualification)" value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} className={inputCls} data-testid="commercial-phone" />
              <textarea rows={3} placeholder="What are you looking to achieve? (optional)" value={form.message} onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))} className={`${inputCls} resize-none`} data-testid="commercial-message" />
              <button type="submit" disabled={sending} data-testid="commercial-submit" className="bg-[#D9A441] hover:bg-[#c8943a] text-black px-8 py-4 font-plexmono text-[11px] uppercase tracking-[0.2em] font-semibold flex items-center gap-2 disabled:opacity-50 transition-colors">
                {sending ? "Sending…" : "Submit Enquiry"}
                <ArrowRight size={15} />
              </button>
            </form>
          </aside>
        </div>
      )}
    </div>
  );
}
